import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Sidebar from '../../components/navigation/Sidebar';
import NavigationBreadcrumb from '../../components/navigation/NavigationBreadcrumb';
import NotificationIndicator from '../../components/navigation/NotificationIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import UserTableRow from './components/UserTableRow';
import UserFormModal from './components/UserFormModal';
import SystemConfigPanel from './components/SystemConfigPanel';
import AuditLogViewer from './components/AuditLogViewer';
import StatsOverview from './components/StatsOverview';

const UserAdministration = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const [direction, setDirection] = useState(() => {
    return localStorage.getItem('direction') || 'ltr';
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [users, setUsers] = useState([
    {
      id: 1,
      name: 'Ahmed Benali',
      email: 'ahmed.benali@legaloffice.ma',
      phone: '+212 661 234 567',
      role: 'administrator',
      status: 'active',
      interfaceLanguage: 'fr',
      lastLogin: '2026-01-27T18:30:00',
      permissions: ['client_view', 'client_edit', 'case_view', 'case_edit', 'billing_view', 'billing_edit', 'document_view', 'document_edit', 'admin_access']
    },
    {
      id: 2,
      name: 'Fatima Zahra',
      email: 'fatima.zahra@legaloffice.ma',
      phone: '+212 662 345 678',
      role: 'lawyer',
      status: 'active',
      interfaceLanguage: 'ar',
      lastLogin: '2026-01-27T17:45:00',
      permissions: ['client_view', 'client_edit', 'case_view', 'case_edit', 'billing_view', 'document_view', 'document_edit']
    },
    {
      id: 3,
      name: 'Youssef Alami',
      email: 'youssef.alami@legaloffice.ma',
      phone: '+212 663 456 789',
      role: 'lawyer',
      status: 'active',
      interfaceLanguage: 'fr',
      lastLogin: '2026-01-27T16:20:00',
      permissions: ['client_view', 'client_edit', 'case_view', 'case_edit', 'billing_view', 'document_view', 'document_edit']
    },
    {
      id: 4,
      name: 'Laila Mansouri',
      email: 'laila.mansouri@legaloffice.ma',
      phone: '+212 664 567 890',
      role: 'lawyer',
      status: 'active',
      interfaceLanguage: 'fr',
      lastLogin: '2026-01-27T15:10:00',
      permissions: ['client_view', 'client_edit', 'case_view', 'case_edit', 'billing_view', 'document_view', 'document_edit']
    },
    {
      id: 5,
      name: 'Karim El Fassi',
      email: 'karim.elfassi@legaloffice.ma',
      phone: '+212 665 678 901',
      role: 'lawyer',
      status: 'active',
      interfaceLanguage: 'en',
      lastLogin: '2026-01-27T14:30:00',
      permissions: ['client_view', 'client_edit', 'case_view', 'case_edit', 'billing_view', 'document_view', 'document_edit']
    },
    {
      id: 6,
      name: 'Samira Idrissi',
      email: 'samira.idrissi@legaloffice.ma',
      phone: '+212 666 789 012',
      role: 'secretary',
      status: 'active',
      interfaceLanguage: 'fr',
      lastLogin: '2026-01-27T13:15:00',
      permissions: ['client_view', 'case_view', 'document_view']
    }
  ]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const labels = {
    pageTitle: {
      en: 'User Administration - LegalOfficeManager',
      fr: 'Administration des utilisateurs - LegalOfficeManager',
      ar: 'إدارة المستخدمين - LegalOfficeManager'
    },
    title: {
      en: 'User Administration',
      fr: 'Administration des utilisateurs',
      ar: 'إدارة المستخدمين'
    },
    subtitle: {
      en: 'Manage users, roles, and system configuration',
      fr: 'Gérer les utilisateurs, les rôles et la configuration du système',
      ar: 'إدارة المستخدمين والأدوار وتكوين النظام'
    },
    tabs: {
      users: {
        en: 'Users',
        fr: 'Utilisateurs',
        ar: 'المستخدمون'
      },
      config: {
        en: 'System Config',
        fr: 'Configuration',
        ar: 'التكوين'
      },
      audit: {
        en: 'Audit Log',
        fr: 'Journal d\'audit',
        ar: 'سجل التدقيق'
      }
    },
    addUser: {
      en: 'Add User',
      fr: 'Ajouter un utilisateur',
      ar: 'إضافة مستخدم'
    },
    search: {
      en: 'Search users...',
      fr: 'Rechercher des utilisateurs...',
      ar: 'البحث عن المستخدمين...'
    },
    filterRole: {
      en: 'Filter by Role',
      fr: 'Filtrer par rôle',
      ar: 'تصفية حسب الدور'
    },
    filterStatus: {
      en: 'Filter by Status',
      fr: 'Filtrer par statut',
      ar: 'تصفية حسب الحالة'
    },
    tableHeaders: {
      user: {
        en: 'User',
        fr: 'Utilisateur',
        ar: 'المستخدم'
      },
      role: {
        en: 'Role',
        fr: 'Rôle',
        ar: 'الدور'
      },
      permissions: {
        en: 'Permissions',
        fr: 'Autorisations',
        ar: 'الصلاحيات'
      },
      lastLogin: {
        en: 'Last Login',
        fr: 'Dernière connexion',
        ar: 'آخر تسجيل دخول'
      },
      status: {
        en: 'Status',
        fr: 'Statut',
        ar: 'الحالة'
      },
      actions: {
        en: 'Actions',
        fr: 'Actions',
        ar: 'الإجراءات'
      }
    },
    deleteConfirm: {
      title: {
        en: 'Delete User',
        fr: 'Supprimer l\'utilisateur',
        ar: 'حذف المستخدم'
      },
      message: {
        en: 'Are you sure you want to delete this user? This action cannot be undone.',
        fr: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action ne peut pas être annulée.',
        ar: 'هل أنت متأكد من رغبتك في حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.'
      },
      cancel: {
        en: 'Cancel',
        fr: 'Annuler',
        ar: 'إلغاء'
      },
      confirm: {
        en: 'Delete',
        fr: 'Supprimer',
        ar: 'حذف'
      }
    }
  };

  const roleOptions = [
    { value: 'all', label: language === 'en' ? 'All Roles' : language === 'fr' ? 'Tous les rôles' : 'جميع الأدوار' },
    { value: 'administrator', label: language === 'en' ? 'Administrator' : language === 'fr' ? 'Administrateur' : 'مدير' },
    { value: 'lawyer', label: language === 'en' ? 'Lawyer' : language === 'fr' ? 'Avocat' : 'محامي' },
    { value: 'secretary', label: language === 'en' ? 'Secretary' : language === 'fr' ? 'Secrétaire' : 'سكرتير' }
  ];

  const statusOptions = [
    { value: 'all', label: language === 'en' ? 'All Status' : language === 'fr' ? 'Tous les statuts' : 'جميع الحالات' },
    { value: 'active', label: language === 'en' ? 'Active' : language === 'fr' ? 'Actif' : 'نشط' },
    { value: 'inactive', label: language === 'en' ? 'Inactive' : language === 'fr' ? 'Inactif' : 'غير نشط' },
    { value: 'suspended', label: language === 'en' ? 'Suspended' : language === 'fr' ? 'Suspendu' : 'معلق' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setUsers(users?.filter(u => u?.id !== userToDelete?.id));
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleToggleStatus = (user) => {
    setUsers(users?.map(u => 
      u?.id === user?.id 
        ? { ...u, status: u?.status === 'active' ? 'inactive' : 'active' }
        : u
    ));
  };

  const handleSubmitUser = (formData) => {
    if (selectedUser) {
      setUsers(users?.map(u => 
        u?.id === selectedUser?.id 
          ? { ...u, ...formData }
          : u
      ));
    } else {
      const newUser = {
        id: users?.length + 1,
        ...formData,
        lastLogin: new Date()?.toISOString()
      };
      setUsers([...users, newUser]);
    }
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <>
      <Helmet>
        <title>{labels?.pageTitle?.[language]}</title>
        <meta name="description" content="Comprehensive user administration and system configuration for law firm management" />
      </Helmet>
      <div className="min-h-screen bg-background" style={{ direction }}>
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <div 
          className={`transition-all duration-250 ${
            sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
          }`}
          style={{ direction }}
        >
          <header className="sticky top-0 z-30 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <NavigationBreadcrumb language={language} />
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mt-2">
                  {labels?.title?.[language]}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  {labels?.subtitle?.[language]}
                </p>
              </div>
              <NotificationIndicator language={language} />
            </div>
          </header>

          <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <StatsOverview language={language} direction={direction} />

            <div className="mt-6 md:mt-8">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'users' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {labels?.tabs?.users?.[language]}
                </button>
                <button
                  onClick={() => setActiveTab('config')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'config' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {labels?.tabs?.config?.[language]}
                </button>
                <button
                  onClick={() => setActiveTab('audit')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'audit' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {labels?.tabs?.audit?.[language]}
                </button>
              </div>

              {activeTab === 'users' && (
                <div className="bg-card border border-border rounded-lg">
                  <div className="px-4 md:px-6 py-4 border-b border-border">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1 max-w-md">
                        <Input
                          type="search"
                          placeholder={labels?.search?.[language]}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <Select
                          options={roleOptions}
                          value={roleFilter}
                          onChange={setRoleFilter}
                          className="w-full sm:w-48"
                        />
                        <Select
                          options={statusOptions}
                          value={statusFilter}
                          onChange={setStatusFilter}
                          className="w-full sm:w-48"
                        />
                        <Button
                          variant="default"
                          iconName="Plus"
                          iconPosition="left"
                          onClick={handleAddUser}
                          fullWidth
                          className="sm:w-auto"
                        >
                          {labels?.addUser?.[language]}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full hidden lg:table">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.user?.[language]}
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.role?.[language]}
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.permissions?.[language]}
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.lastLogin?.[language]}
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.status?.[language]}
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {labels?.tableHeaders?.actions?.[language]}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredUsers?.map((user) => (
                          <UserTableRow
                            key={user?.id}
                            user={user}
                            onEdit={handleEditUser}
                            onDelete={handleDeleteUser}
                            onToggleStatus={handleToggleStatus}
                            language={language}
                            direction={direction}
                          />
                        ))}
                      </tbody>
                    </table>

                    <div className="lg:hidden p-4">
                      {filteredUsers?.map((user) => (
                        <UserTableRow
                          key={user?.id}
                          user={user}
                          onEdit={handleEditUser}
                          onDelete={handleDeleteUser}
                          onToggleStatus={handleToggleStatus}
                          language={language}
                          direction={direction}
                        />
                      ))}
                    </div>
                  </div>

                  {filteredUsers?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <Icon name="Users" size={48} className="text-muted-foreground mb-4" />
                      <p className="text-muted-foreground text-center">
                        {language === 'en' ? 'No users found' : language === 'fr' ? 'Aucun utilisateur trouvé' : 'لم يتم العثور على مستخدمين'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'config' && (
                <SystemConfigPanel language={language} direction={direction} />
              )}

              {activeTab === 'audit' && (
                <AuditLogViewer language={language} direction={direction} />
              )}
            </div>
          </main>
        </div>
      </div>
      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleSubmitUser}
        user={selectedUser}
        language={language}
        direction={direction}
      />
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
            <div 
              className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-md"
              style={{ direction }}
            >
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">{labels?.deleteConfirm?.title?.[language]}</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-muted-foreground">{labels?.deleteConfirm?.message?.[language]}</p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/50">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {labels?.deleteConfirm?.cancel?.[language]}
                </Button>
                <Button
                  variant="destructive"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={confirmDelete}
                >
                  {labels?.deleteConfirm?.confirm?.[language]}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UserAdministration;